import { computed, reactive, readonly } from 'vue';
import { apiRequest } from '@/lib/client-api';
import { useClientSession } from '@/composables/use-client-session';

const state = reactive({
  items: [],
  loading: false,
  loaded: false,
});

function normalizeItem(item) {
  return {
    id: item.id,
    name: item.name || '',
    price: item.price || '',
    image: item.image || '',
    tag: item.tag || 'Product',
    product_url: item.product_url || `/product-details/${item.id}`,
  };
}

async function load(force = false) {
  const session = useClientSession();

  if (!session.token.value) {
    state.items = [];
    state.loaded = true;
    return [];
  }

  if (state.loaded && !force) {
    return state.items;
  }

  state.loading = true;

  try {
    const response = await apiRequest('/wishlist', { token: session.token.value });
    state.items = (response.data || []).map(normalizeItem);
    state.loaded = true;
    return state.items;
  } finally {
    state.loading = false;
  }
}

async function add(productId) {
  const session = useClientSession();
  const response = await apiRequest('/wishlist', {
    method: 'POST',
    token: session.token.value,
    data: { product_id: productId },
  });

  const nextItem = normalizeItem(response.data);

  if (!state.items.some((item) => item.id === nextItem.id)) {
    state.items = [nextItem, ...state.items];
  }

  state.loaded = true;
  return nextItem;
}

async function remove(productId) {
  const session = useClientSession();
  await apiRequest(`/wishlist/${productId}`, {
    method: 'DELETE',
    token: session.token.value,
  });

  state.items = state.items.filter((item) => item.id !== productId);
  state.loaded = true;
}

function reset() {
  state.items = [];
  state.loading = false;
  state.loaded = false;
}

function isInWishlist(productId) {
  return state.items.some((item) => item.id === productId);
}

export function useWishlist() {
  return {
    state: readonly(state),
    items: computed(() => state.items),
    count: computed(() => state.items.length),
    loading: computed(() => state.loading),
    load,
    add,
    remove,
    reset,
    isInWishlist,
  };
}
